import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Build a full URL for an uploaded/media path (leaves external http URLs untouched).
// Pass `width` to request a smaller, display-sized variant for cards/thumbnails.
export const mediaUrl = (path, width) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  const url = `${BACKEND_URL}${path}`;
  if (width && path.startsWith("/api/uploads/")) {
    return `${url}${path.includes("?") ? "&" : "?"}w=${width}`;
  }
  return url;
};

// Responsive srcset for our own uploaded images so phones fetch a smaller file.
// Returns undefined for external URLs (can't resize those).
export const mediaSrcSet = (path, widths = [480, 768, 1080, 1440]) => {
  if (!path || path.startsWith("http") || !path.startsWith("/api/uploads/")) return undefined;
  return widths.map((w) => `${mediaUrl(path, w)} ${w}w`).join(", ");
};

const api = axios.create({ baseURL: API });

// Attach token if present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("cass_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---------------- Auth ----------------
export const authApi = {
  login: (username, password) => api.post("/auth/login", { username, password }).then((r) => r.data),
  verify2fa: (challenge_token, code) => api.post("/auth/2fa/verify", { challenge_token, code }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  listUsers: () => api.get("/auth/users").then((r) => r.data),
  createUser: (data) => api.post("/auth/users", data).then((r) => r.data),
  deleteUser: (id) => api.delete(`/auth/users/${id}`).then((r) => r.data),
  notifyLoginUrl: () => api.post(`/auth/users/notify-login-url`).then((r) => r.data),
  twofa: {
    status: () => api.get("/auth/2fa/status").then((r) => r.data),
    setup: (method) => api.post("/auth/2fa/setup", { method }).then((r) => r.data),
    enable: (method, code) => api.post("/auth/2fa/enable", { method, code }).then((r) => r.data),
    disable: (password) => api.post("/auth/2fa/disable", { password }).then((r) => r.data),
  },
};

// ---------------- Public reads ----------------
let _googleRatingPromise = null; // session cache for the Google rating/reviews fetch
let _packagePagePromise = null; // session cache for the package page config/visibility
let _askCassieStatusPromise = null; // session cache for the Ask Cassie launch/retirement state
let _pricingPromise = null; // session cache: the header needs the pricing page's visibility too
let _navigationPromise = null; // session cache for the editable header menu structure
export const publicApi = {
  services: () => api.get("/services").then((r) => r.data),
  serviceGroups: () => api.get("/service-groups").then((r) => r.data),
  homepage: () => api.get("/homepage").then((r) => r.data),
  footer: () => api.get("/footer").then((r) => r.data),
  bookingLink: (data) => api.post("/booking-link", data).then((r) => r.data),
  service: (slug) => api.get(`/services/${slug}`).then((r) => r.data),
  blog: () => api.get("/blog").then((r) => r.data),
  blogPost: (slug) => api.get(`/blog/${slug}`).then((r) => r.data),
  podcasts: () => api.get("/podcasts").then((r) => r.data),
  allPodcasts: () => api.get("/podcasts/all").then((r) => r.data),
  videoTestimonials: () => api.get("/testimonials/video").then((r) => r.data),
  writtenTestimonials: () => api.get("/testimonials/written").then((r) => r.data),
  staff: () => api.get("/staff").then((r) => r.data),
  pages: () => api.get("/pages").then((r) => r.data),
  page: (slug) => api.get(`/pages/${slug}`).then((r) => r.data),
  series: () => api.get("/podcast-series").then((r) => r.data),
  seriesDetail: (slug) => api.get(`/podcast-series/${slug}`).then((r) => r.data),
  settings: () => api.get("/settings").then((r) => r.data),
  pricing: () => {
    if (!_pricingPromise) {
      _pricingPromise = api.get("/pricing").then((r) => r.data).catch((e) => {
        _pricingPromise = null;
        throw e;
      });
    }
    return _pricingPromise;
  },
  meetingRoom: () => api.get("/meeting-room").then((r) => r.data),
  chatLinkClick: (data) => api.post("/chat/link-click", data).then((r) => r.data).catch(() => {}),
  about: () => api.get("/about").then((r) => r.data),
  guides: () => api.get("/guides").then((r) => r.data),
  caseStudies: () => api.get("/case-studies").then((r) => r.data),
  caseStudy: (slug) => api.get(`/case-studies/${slug}`).then((r) => r.data),
  promo: () => api.get("/promo").then((r) => r.data),
  trackPromo: (event, extra = {}) => api.post("/promo/track", { event, ...extra }).then((r) => r.data).catch(() => {}),
  downloadGuide: (id, data) => api.post(`/guides/${id}/download`, data).then((r) => r.data),
  downloadLeadMagnet: (data) => api.post(`/legal-cashiering/lead-magnet/download`, data).then((r) => r.data),
  chat: (data) => api.post("/chat", data).then((r) => r.data),
  contact: (data) => api.post("/contact", data).then((r) => r.data),
  submitPageForm: (data) => api.post("/pages/form-submit", data).then((r) => r.data),
  quote: (data) => api.post("/quote", data).then((r) => r.data),
  applyCareers: (formData) => api.post("/careers/apply", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  vacancies: () => api.get("/careers/vacancies").then((r) => r.data),
  googleRating: () => {
    // Cache the Google rating/reviews for the browser session so navigating
    // across pages (which each surface reviews) only fetches it once.
    if (!_googleRatingPromise) {
      _googleRatingPromise = api.get("/google-rating").then((r) => r.data).catch((e) => {
        _googleRatingPromise = null; // allow a retry on failure
        throw e;
      });
    }
    return _googleRatingPromise;
  },
  subscribe: (data) => api.post("/subscribers", data).then((r) => r.data),
  faqs: () => api.get("/faqs").then((r) => r.data),
  healthCheck: () => api.get("/health-check").then((r) => r.data),
  submitHealthCheck: (data) => api.post("/health-check/submit", data).then((r) => r.data),
  getAssessment: (slug) => api.get(`/assessments/${slug}`).then((r) => r.data),
  assessmentsList: () => api.get("/assessments").then((r) => r.data),
  submitAssessment: (slug, data) => api.post(`/assessments/${slug}/submit`, data).then((r) => r.data),
  trackAssessment: (slug, event, section_index) => api.post(`/assessments/${slug}/track`, { event, section_index }).then((r) => r.data).catch(() => {}),
  assessmentPdf: (slug, data) => api.post(`/assessments/${slug}/report-pdf`, data, { responseType: "blob" }).then((r) => r.data),
  legalCashiering: () => api.get("/legal-cashiering").then((r) => r.data),
  cassiePrompts: () => api.get("/cassie-prompts").then((r) => r.data),
  askCassieHub: () => api.get("/ask-cassie/hub").then((r) => r.data),
  askCassieAnswer: (slug) => api.get(`/ask-cassie/answers/${slug}`).then((r) => r.data),
  askCassieResolve: (slug) => api.get(`/ask-cassie/resolve/${slug}`).then((r) => r.data),
  askCassieReviewer: (slug) => api.get(`/ask-cassie/reviewer/${slug}`).then((r) => r.data),
  // Session-cached: the header, the footer and the old FAQ page all need it.
  askCassieStatus: () => {
    if (!_askCassieStatusPromise) {
      _askCassieStatusPromise = api.get("/ask-cassie/status").then((r) => r.data).catch((e) => {
        _askCassieStatusPromise = null;
        throw e;
      });
    }
    return _askCassieStatusPromise;
  },
  askCassieRelated: (params) => api.get("/ask-cassie/related", { params }).then((r) => r.data),
  askCassieUnanswered: (term, source = "hub") =>
    api.post("/ask-cassie/unanswered", { term, source }).then((r) => r.data),
  leadSources: () => api.get("/lead-sources").then((r) => r.data),
  bookingIntent: (body) => api.post("/booking/intent", body).then((r) => r.data).catch(() => null),
  bookingConfirm: (body) => api.post("/booking/confirm", body).then((r) => r.data).catch(() => null),
  pricingHelpEvent: (key) =>
    api.post("/pricing/help-event", { key }).then((r) => r.data).catch(() => null),
  askCassieTrack: (body) => api.post("/ask-cassie/track", body).then((r) => r.data),
  shareMeta: (path) => api.get(`/share/meta?path=${encodeURIComponent(path)}`).then((r) => r.data),
  shareEvent: (body) => api.post("/share/event", body).then((r) => r.data).catch(() => {}),
  townTestimonials: () => api.get("/town-testimonials").then((r) => r.data),
  calculatorConfig: () => api.get("/calculator/config").then((r) => r.data),
  trackCalculator: (event, meta) => api.post("/calculator/track", { event, meta }).then((r) => r.data).catch(() => {}),
  calculatorReport: (data) => api.post("/calculator/report", data, { responseType: "blob" }).then((r) => r.data),
  calculatorAiSummary: (facts) => api.post("/calculator/ai-summary", { facts }).then((r) => r.data),
  trackAnnouncement: (variant, event) => api.post("/announcement/track", { variant, event }).then((r) => r.data).catch(() => {}),
  mtdConfig: () => api.get("/mtd/config").then((r) => r.data),
  mtdCheck: (data) => api.post("/mtd/check", data).then((r) => r.data),
  taxDates: () => api.get("/tax-dates").then((r) => r.data),
  taxDateReminder: (data) => api.post("/tax-dates/reminders", data).then((r) => r.data),
  // Cached for the session: the header and every service page need the package
  // page's visibility, so this must not be a request per component.
  packagePage: () => {
    if (!_packagePagePromise) {
      _packagePagePromise = api.get("/package-page").then((r) => r.data).catch((e) => {
        _packagePagePromise = null;
        throw e;
      });
    }
    return _packagePagePromise;
  },
  xeroPricing: () => api.get("/xero-pricing").then((r) => r.data),
  // Session-cached: the header and mobile menu both render from the saved structure.
  navigation: () => {
    if (!_navigationPromise) {
      _navigationPromise = api.get("/navigation").then((r) => r.data).catch((e) => {
        _navigationPromise = null;
        throw e;
      });
    }
    return _navigationPromise;
  },
};

// ---------------- Admin CRUD ----------------
const crud = (base) => ({
  list: () => api.get(base).then((r) => r.data),
  create: (data) => api.post(base, data).then((r) => r.data),
  update: (id, data) => api.put(`${base}/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`${base}/${id}`).then((r) => r.data),
});

export const adminApi = {
  blog: { ...crud("/blog"), list: () => api.get("/blog?all=true").then((r) => r.data) },
  podcasts: { ...crud("/podcasts"), list: () => api.get("/admin/podcasts").then((r) => r.data), reorder: (ids) => api.post("/podcasts/reorder", { ids }).then((r) => r.data), broadcast: (id, site_url) => api.post(`/podcasts/${id}/broadcast`, { site_url }).then((r) => r.data), broadcastPreview: (id, site_url) => api.post(`/podcasts/${id}/broadcast-preview`, { site_url }).then((r) => r.data) },
  videoTestimonials: crud("/testimonials/video"),
  writtenTestimonials: crud("/testimonials/written"),
  services: crud("/services"),
  serviceGroups: crud("/service-groups"),
  staff: crud("/staff"),
  staffReorder: (ids) => api.post("/staff/reorder", { ids }).then((r) => r.data),
  serviceGroupsReorder: (ids) => api.post("/service-groups/reorder", { ids }).then((r) => r.data),
  servicesReorder: (items) => api.post("/services/reorder", { items }).then((r) => r.data),
  caseStudiesReorder: (ids) => api.post("/case-studies/reorder", { ids }).then((r) => r.data),
  guidesReorder: (ids) => api.post("/guides/reorder", { ids }).then((r) => r.data),
  pages: {
    list: () => api.get("/admin/pages").then((r) => r.data),
    get: (id) => api.get(`/admin/pages/${id}`).then((r) => r.data),
    create: (data) => api.post("/pages", data).then((r) => r.data),
    update: (id, data) => api.put(`/pages/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/pages/${id}`).then((r) => r.data),
  },
  navigation: {
    get: () => api.get("/admin/navigation").then((r) => r.data),
    save: (items) => api.put("/admin/navigation", { items }).then((r) => r.data),
    reset: () => api.post("/admin/navigation/reset").then((r) => r.data),
    groups: () => api.get("/admin/navigation/groups").then((r) => r.data),
  },
  series: { ...crud("/podcast-series"), reorder: (ids) => api.post("/podcast-series/reorder", { ids }).then((r) => r.data) },
  enquiries: {
    list: () => api.get("/enquiries").then((r) => r.data),
    remove: (id) => api.delete(`/enquiries/${id}`).then((r) => r.data),
    setDealt: (id, dealt_with) => api.patch(`/enquiries/${id}`, { dealt_with }).then((r) => r.data),
  },
  settings: {
    get: () => api.get("/settings").then((r) => r.data),
    update: (data) => api.put("/settings", data).then((r) => r.data),
  },
  subscribers: {
    list: () => api.get("/subscribers").then((r) => r.data),
    remove: (id) => api.delete(`/subscribers/${id}`).then((r) => r.data),
  },
  pricing: {
    get: () => api.get("/pricing").then((r) => r.data),
    update: (data) => api.put("/pricing", data).then((r) => r.data),
  },
  meetingRoom: {
    get: () => api.get("/meeting-room").then((r) => r.data),
    update: (data) => api.put("/meeting-room", data).then((r) => r.data),
  },
  about: {
    get: () => api.get("/about").then((r) => r.data),
    update: (data) => api.put("/about", data).then((r) => r.data),
  },
  guides: { ...crud("/guides"), list: () => api.get("/guides?all=true").then((r) => r.data) },
  contentTemplates: crud("/content-templates"),
  caseStudies: crud("/case-studies"),
  promo: {
    get: () => api.get("/admin/promo").then((r) => r.data),
    update: (data) => api.put("/promo", data).then((r) => r.data),
    stats: () => api.get("/promo/stats").then((r) => r.data),
  },
  shareCards: {
    get: () => api.get("/admin/share-cards").then((r) => r.data),
    update: (body) => api.put("/admin/share-cards", body).then((r) => r.data),
    pages: () => api.get("/admin/share-cards/pages").then((r) => r.data),
    report: (days = 30) => api.get(`/admin/share/report?days=${days}`).then((r) => r.data),
  },
  chatFaqs: {
    ...crud("/chat-faqs"),
    importCsv: (formData) => api.post("/chat-faqs/import", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  },
  chatMessages: {
    list: () => api.get("/chat-messages").then((r) => r.data),
    linkClicks: () => api.get("/chat-messages/link-clicks").then((r) => r.data),
    remove: (id) => api.delete(`/admin/chat-messages/${id}`).then((r) => r.data),
    removeSession: (sessionId) => api.delete(`/admin/chat-sessions/${sessionId}`).then((r) => r.data),
  },
  retention: {
    get: () => api.get("/admin/privacy/retention").then((r) => r.data),
    save: (data) => api.put("/admin/privacy/retention", data).then((r) => r.data),
    purge: (dry_run = false) => api.post("/admin/privacy/retention/purge", { dry_run }).then((r) => r.data),
  },
  cassiePrompts: {
    get: () => api.get("/cassie-prompts").then((r) => r.data),
    update: (data) => api.put("/cassie-prompts", data).then((r) => r.data),
  },
  townTestimonials: {
    get: () => api.get("/town-testimonials").then((r) => r.data),
    update: (data) => api.put("/town-testimonials", data).then((r) => r.data),
  },
  announcementStats: {
    get: () => api.get("/announcement/stats").then((r) => r.data),
    reset: () => api.post("/announcement/stats/reset").then((r) => r.data),
  },
  guideLeads: {
    list: () => api.get("/guide-leads").then((r) => r.data),
    remove: (id) => api.delete(`/guide-leads/${id}`).then((r) => r.data),
  },
  quotes: {
    list: () => api.get("/quotes").then((r) => r.data),
    remove: (id) => api.delete(`/quotes/${id}`).then((r) => r.data),
    setDealt: (id, dealt_with) => api.patch(`/quotes/${id}`, { dealt_with }).then((r) => r.data),
  },
  jobApplications: {
    list: () => api.get("/job-applications").then((r) => r.data),
    remove: (id) => api.delete(`/job-applications/${id}`).then((r) => r.data),
    setDealt: (id, dealt_with) => api.patch(`/job-applications/${id}`, { dealt_with }).then((r) => r.data),
    setStatus: (id, status, send_email = false) => api.patch(`/job-applications/${id}/status`, { status, send_email }).then((r) => r.data),
  },
  vacancies: {
    list: () => api.get("/vacancies").then((r) => r.data),
    create: (data) => api.post("/vacancies", data).then((r) => r.data),
    update: (id, data) => api.put(`/vacancies/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/vacancies/${id}`).then((r) => r.data),
  },
  careersSettings: {
    get: () => api.get("/careers/settings").then((r) => r.data),
    update: (data) => api.put("/careers/settings", data).then((r) => r.data),
  },
  uploadImage: (formData) =>
    api.post("/uploads", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  uploadDocument: (formData) =>
    api.post("/uploads/document", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  // Chunked video upload -> Emergent Object Storage. Returns { id, url }.
  uploadVideo: async (file, onProgress) => {
    const CHUNK = 5 * 1024 * 1024;
    const { upload_id } = await api
      .post("/uploads/video/init", { filename: file.name, content_type: file.type || "video/mp4", total_size: file.size })
      .then((r) => r.data);
    const total = Math.ceil(file.size / CHUNK);
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("upload_id", upload_id);
      fd.append("index", i);
      fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
      await api.post("/uploads/video/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (onProgress) onProgress(Math.round(((i + 1) / total) * 100));
    }
    return api.post("/uploads/video/complete", { upload_id }).then((r) => r.data);
  },
  analytics: () => api.get("/analytics").then((r) => r.data),
  emailAnalytics: () => api.post("/analytics/email").then((r) => r.data),
  resetAnalytics: () => api.post("/analytics/reset").then((r) => r.data),
  homepage: {
    get: () => api.get("/homepage").then((r) => r.data),
    save: (data) => api.put("/homepage", data).then((r) => r.data),
  },
  healthCheck: {
    get: () => api.get("/admin/health-check").then((r) => r.data),
    save: (data) => api.put("/health-check", data).then((r) => r.data),
    leads: () => api.get("/health-check/leads").then((r) => r.data),
    removeLead: (id) => api.delete(`/health-check/leads/${id}`).then((r) => r.data),
  },
  notificationRecipients: {
    list: () => api.get("/notification-recipients").then((r) => r.data),
    add: (data) => api.post("/notification-recipients", data).then((r) => r.data),
    update: (id, data) => api.put(`/notification-recipients/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/notification-recipients/${id}`).then((r) => r.data),
  },
  footer: {
    get: () => api.get("/footer").then((r) => r.data),
    save: (data) => api.put("/footer", data).then((r) => r.data),
  },
  xeroPricing: {
    get: () => api.get("/xero-pricing").then((r) => r.data),
    save: (data) => api.put("/xero-pricing", data).then((r) => r.data),
    confirm: () => api.post("/xero-pricing/confirm").then((r) => r.data),
  },
  legalCashiering: {
    get: () => api.get("/legal-cashiering").then((r) => r.data),
    save: (data) => api.put("/legal-cashiering", data).then((r) => r.data),
    leadMagnetStats: () => api.get("/legal-cashiering/lead-magnet/stats").then((r) => r.data),
    leadMagnetLeads: () => api.get("/legal-cashiering/lead-magnet/leads").then((r) => r.data),
  },
  mailchimp: {
    status: () => api.get("/mailchimp/status").then((r) => r.data),
    syncAll: () => api.post("/mailchimp/sync-all").then((r) => r.data),
  },
  gbp: {
    status: () => api.get("/gbp/status").then((r) => r.data),
    apiStatus: () => api.get("/gbp/api-status").then((r) => r.data),
    accounts: () => api.get("/gbp/accounts").then((r) => r.data),
    select: (data) => api.post("/gbp/select", data).then((r) => r.data),
    disconnect: () => api.post("/gbp/disconnect").then((r) => r.data),
    postBlog: (id) => api.post(`/blog/${id}/post-to-gbp`).then((r) => r.data),
    startUrl: () => `${API}/gbp/oauth/start?token=${encodeURIComponent(localStorage.getItem("cass_token") || "")}`,
  },
  // One consolidated Google reporting namespace. Detail tabs still call the
  // existing endpoints directly; this is only the roll-up and the health check.
  reporting: {
    overview: (days) => api.get("/admin/reporting/overview", { params: { days } }).then((r) => r.data),
    health: () => api.get("/admin/reporting/health").then((r) => r.data),
    ga4Status: () => api.get("/admin/ga4/status").then((r) => r.data),
    ga4Report: (days) => api.get("/admin/ga4/report", { params: { days } }).then((r) => r.data),
    ga4: (days) => api.get("/admin/reporting/ga4", { params: { days } }).then((r) => r.data),
  },
  ga4: {
    status: () => api.get("/admin/ga4/status").then((r) => r.data),
    uploadKey: (formData) =>
      api.post("/admin/ga4/credentials", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
    deleteKey: () => api.delete("/admin/ga4/credentials").then((r) => r.data),
    saveMeasurementSecret: (secret) => api.post("/admin/ga4/measurement-secret", { secret }).then((r) => r.data),
    deleteMeasurementSecret: () => api.delete("/admin/ga4/measurement-secret").then((r) => r.data),
    replayBookings: () => api.post("/admin/ga4/replay-bookings").then((r) => r.data),
    verifyServerTracking: () => api.post("/admin/ga4/verify-server-tracking", {}, { timeout: 60000 }).then((r) => r.data),
  },
  googleAds: {
    status: () => api.get("/admin/google-ads/status").then((r) => r.data),
    report: (days) => api.get("/admin/reporting/google-ads", { params: { days } }).then((r) => r.data),
    save: (data) => api.post("/admin/google-ads/credentials", data).then((r) => r.data),
    remove: () => api.delete("/admin/google-ads/credentials").then((r) => r.data),
  },
  gsc: {    status: () => api.get("/gsc/status").then((r) => r.data),
    properties: () => api.get("/gsc/properties").then((r) => r.data),
    setProperty: (site_url) => api.post("/gsc/property", { site_url }).then((r) => r.data),
    performance: (days, dimension) => api.get("/gsc/performance", { params: { days, dimension } }).then((r) => r.data),
    inspect: (url, force) => api.post("/gsc/inspect", { url }, { params: { force: !!force } }).then((r) => r.data),
    disconnect: () => api.post("/gsc/disconnect").then((r) => r.data),
    startUrl: () => `${API}/gsc/oauth/start?token=${encodeURIComponent(localStorage.getItem("cass_token") || "")}`,
  },
  metricool: {
    status: () => api.get("/metricool/status").then((r) => r.data),
    post: (data) => api.post("/metricool/post", data).then((r) => r.data),
    posts: () => api.get("/metricool/posts").then((r) => r.data),
    caption: (data) => api.post("/metricool/caption", data).then((r) => r.data),
    remove: (id) => api.delete(`/metricool/posts/${id}`).then((r) => r.data),
    bestTimes: () => api.get("/metricool/best-times").then((r) => r.data),
    linkPreview: (url) => api.get("/metricool/link-preview", { params: { url } }).then((r) => r.data),
    hashtagSets: () => api.get("/metricool/hashtag-sets").then((r) => r.data),
    saveHashtagSet: (data) => api.post("/metricool/hashtag-sets", data).then((r) => r.data),
    removeHashtagSet: (id) => api.delete(`/metricool/hashtag-sets/${id}`).then((r) => r.data),
    recurring: () => api.get("/metricool/recurring").then((r) => r.data),
    saveRecurring: (data) => api.post("/metricool/recurring", data).then((r) => r.data),
    toggleRecurring: (id) => api.patch(`/metricool/recurring/${id}/toggle`).then((r) => r.data),
    removeRecurring: (id) => api.delete(`/metricool/recurring/${id}`).then((r) => r.data),
    runRecurringNow: (id) => api.post(`/metricool/recurring/${id}/run-now`).then((r) => r.data),
  },
  assessments: {
    list: () => api.get("/admin/assessments").then((r) => r.data),
    get: (slug) => api.get(`/admin/assessments/${slug}`).then((r) => r.data),
    save: (slug, data) => api.put(`/assessments/${slug}`, data).then((r) => r.data),
    leads: (slug) => api.get(`/assessments/${slug}/leads`).then((r) => r.data),
    deleteLead: (slug, id) => api.delete(`/assessments/${slug}/leads/${id}`).then((r) => r.data),
    funnel: (slug) => api.get(`/assessments/${slug}/funnel`).then((r) => r.data),
  },
  assessmentNudge: {
    get: () => api.get("/assessments-nudge/settings").then((r) => r.data),
    save: (data) => api.put("/assessments-nudge/settings", data).then((r) => r.data),
  },
  calculator: {
    getConfig: () => api.get("/admin/calculator/config").then((r) => r.data),
    saveConfig: (data) => api.put("/calculator/config", data).then((r) => r.data),
    funnel: () => api.get("/calculator/funnel").then((r) => r.data),
    submissions: () => api.get("/calculator/submissions").then((r) => r.data),
    removeSubmission: (id) => api.delete(`/calculator/submissions/${id}`).then((r) => r.data),
  },
  mtd: {
    getConfig: () => api.get("/admin/mtd/config").then((r) => r.data),
    saveConfig: (data) => api.put("/mtd/config", data).then((r) => r.data),
    leads: () => api.get("/mtd/leads").then((r) => r.data),
    removeLead: (id) => api.delete(`/mtd/leads/${id}`).then((r) => r.data),
  },
  taxDates: {
    getConfig: () => api.get("/admin/tax-dates").then((r) => r.data),
    saveConfig: (data) => api.put("/tax-dates", data).then((r) => r.data),
    reminders: () => api.get("/tax-dates/reminders").then((r) => r.data),
    removeReminder: (id) => api.delete(`/tax-dates/reminders/${id}`).then((r) => r.data),
  },
  askCassie: {
    answers: () => api.get("/admin/ask-cassie/answers").then((r) => r.data),
    options: () => api.get("/admin/ask-cassie/options").then((r) => r.data),
    create: (data) => api.post("/ask-cassie/answers", data).then((r) => r.data),
    update: (id, data) => api.put(`/ask-cassie/answers/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/ask-cassie/answers/${id}`).then((r) => r.data),
    regenerate: (data = {}) => api.post("/ask-cassie/regenerate", data).then((r) => r.data),
    approve: (id, data) => api.post(`/ask-cassie/answers/${id}/approve`, data).then((r) => r.data),
    bulk: (data) => api.post("/ask-cassie/answers/bulk", data).then((r) => r.data),
    reviewReport: () => api.get("/admin/ask-cassie/review-report").then((r) => r.data),
    assignCategory: (id, category) => api.put(`/ask-cassie/answers/${id}/category`, { category }).then((r) => r.data),
    setConfig: (data) => api.put("/admin/ask-cassie/config", data).then((r) => r.data),
    backup: () => api.get("/admin/ask-cassie/backup.json").then((r) => r.data),
    restore: (data) => api.post("/admin/ask-cassie/restore", data).then((r) => r.data),
    unanswered: () => api.get("/admin/ask-cassie/unanswered").then((r) => r.data),
    gaps: (days = 90) => api.get(`/admin/ask-cassie/gaps?days=${days}`).then((r) => r.data),
    clearIgnoredGaps: () => api.post("/admin/ask-cassie/gaps/clear-ignored").then((r) => r.data),
    unspamGaps: (ids) => api.post("/admin/ask-cassie/gaps/unspam", { ids }).then((r) => r.data),
    deleteSpamGaps: () => api.post("/admin/ask-cassie/gaps/delete-spam").then((r) => r.data),
    resolveGaps: (ids, action) => api.post("/admin/ask-cassie/gaps/resolve", { ids, action }).then((r) => r.data),
    removeUnanswered: (id) => api.delete(`/admin/ask-cassie/unanswered/${id}`).then((r) => r.data),
    exportCsv: () => api.get("/admin/ask-cassie/export.csv", { responseType: "text" }).then((r) => r.data),
    templateCsv: () => api.get("/admin/ask-cassie/template.csv", { responseType: "text" }).then((r) => r.data),
    importCsv: (data) => api.post("/admin/ask-cassie/import", data).then((r) => r.data),
    faqMigration: () => api.get("/admin/ask-cassie/faq-migration").then((r) => r.data),
    auditStatus: () => api.get("/admin/ask-cassie/audit-2026-10").then((r) => r.data),
    newAnswersStatus: () => api.get("/admin/ask-cassie/new-answers").then((r) => r.data),
    stats: (days = 30) => api.get(`/admin/ask-cassie/stats?days=${days}`).then((r) => r.data),
    newAnswersImport: (dry_run = false) => api.post("/admin/ask-cassie/new-answers/import", { dry_run }).then((r) => r.data),
    auditApply: (dry_run = false) => api.post("/admin/ask-cassie/audit-2026-10/apply", { dry_run }).then((r) => r.data),
    faqMigrationCsv: (file) => api.get(`/admin/ask-cassie/faq-migration.csv?file=${file}`, { responseType: "text" }).then((r) => r.data),
  },
  leadSources: {
    get: () => api.get("/admin/lead-sources").then((r) => r.data),
    save: (options) => api.put("/admin/lead-sources", { options }).then((r) => r.data),
    report: (params) => api.get("/admin/lead-source/report", { params }).then((r) => r.data),
    csv: (params) => api.get("/admin/lead-source/report.csv", { params, responseType: "blob" }).then((r) => r.data),
  },
  calendly: {
    status: () => api.get("/admin/calendly/status").then((r) => r.data),
    reporting: () => api.get("/admin/calendly/reporting").then((r) => r.data),
    sync: (days = 30) => api.post("/admin/calendly/sync", { days }).then((r) => r.data),
    settings: (prefill_position) => api.put("/admin/calendly/settings", { prefill_position }).then((r) => r.data),
    createWebhook: (url) => api.post("/admin/calendly/webhook", { url }).then((r) => r.data),
  },
  packageBuilder: {
    getConfig: () => api.get("/admin/package-page").then((r) => r.data),
    saveConfig: (data) => api.put("/package-page", data).then((r) => r.data),
    insights: () => api.get("/pricing/insights").then((r) => r.data),
    dropOff: () => api.get("/pricing/drop-off").then((r) => r.data),
    resetInsights: () => api.delete("/pricing/insights").then((r) => r.data),
    health: () => api.get("/pricing/health").then((r) => r.data),
    catalogue: () => api.get("/admin/pricing/catalogue").then((r) => r.data),
    sessions: () => api.get("/pricing/sessions").then((r) => r.data),
    session: (sid) => api.get(`/pricing/sessions/${sid}`).then((r) => r.data),
    removeSession: (sid) => api.delete(`/pricing/sessions/${sid}`).then((r) => r.data),
    helpStats: (days = 90) => api.get(`/admin/pricing/help-stats?days=${days}`).then((r) => r.data),
  },
};

export default api;
